import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { isEmpty, partition } from 'lodash'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'

import { Loader } from '@/components/loaders/Loader'
import { FilterBar } from '@/components/filters/FilterBar'

import { AdvancedTablePagination } from './AdvancedTablePagination'
import { AdvancedTableHeader } from './AdvancedTableHeader'
import { AdvancedTableBody } from './AdvancedTableBody'
import NoData from '../../NoData'
import { getComparator, stableSort, filterRows, calculateDataGridOffsets } from './helpers'
import { BulkSelectBanner } from './BulkSelectBanner'

import './AdvancedTable.scss'

/**
 * @import import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
 * @param {object} props
 * @param {string} [props.testid] data-testid on the table element
 * @param {object[]} [props.rows] Row objects keyed by column accessor; a cell value may be `{ as, value, cellProps }` to render a custom node, and a row may carry `rowProps`, `expandedContent`, `expandedRows`, or `childTableData`/`childColumns` for expandable rows
 * @param {object[]} [props.columns] Column definitions: `{ accessor, label, isSortable, isSearchable, searchSchema, sticky, collapsing, noWrap, fullWidthRow, headerAlignment, description, color, formatCopyValue }`
 * @param {boolean} [props.loading] Replaces the table with a centered loader
 * @param {string} [props.loadingText] Label under the loader
 * @param {object[]} [props.actions] Per-row buttons in a trailing Actions column: `{ label, icon, fn(event, row) }`, or `{ label, trigger(row) }` for a custom node
 * @param {string} [props.defaultOrderBy] Accessor the rows are initially sorted by
 * @param {'asc'|'desc'} [props.defaultOrder] Initial sort direction
 * @param {boolean} [props.sortEnabled] Set false to render rows in the order given
 * @param {string} [props.index] Row field used as the unique row id
 * @param {boolean} [props.displayHeader] Set false to hide the header row
 * @param {boolean} [props.pagination] Paginates rows and renders the pagination footer
 * @param {number} [props.rowsPerPage] Initial page size when paginated
 * @param {boolean} [props.enableUpdateRowsPerPage] Shows the page-size selector in the pagination footer
 * @param {boolean} [props.stickyAction] Pins the Actions column to the right edge while scrolling
 * @param {boolean} [props.truncate] Truncates header labels with an ellipsis at a fixed 200px
 * @param {boolean} [props.wrapColumnContent] Keeps body cells on one line; set false to wrap body cells and keep header labels on one line
 * @param {boolean} [props.hoverable] Row hover highlight
 * @param {string} [props.noDataText] Empty-state body text when there are no rows
 * @param {string} [props.noDataHeader] Empty-state heading when there are no rows
 * @param {node} [props.noDataAction] Action rendered under the empty state
 * @param {boolean} [props.striped] Alternating row background
 * @param {boolean} [props.borderlessRows] Removes the borders between rows
 * @param {boolean} [props.celled] Vertical borders between columns
 * @param {boolean} [props.compact] Tighter cell padding
 * @param {boolean} [props.compactHeader] Tighter header cell padding
 * @param {string} [props.pinned] Accessor whose truthy rows always sort to the top
 * @param {boolean} [props.pinnedReverse] Sorts pinned rows to the bottom instead
 * @param {boolean} [props.bulkSelectable] Adds a checkbox column; requires selectedRows, handleBulkSelectSingle, and bulkSelectAction
 * @param {Function} [props.handleBulkSelectSingle] Called with (rowId, row) when a row checkbox is toggled
 * @param {node} [props.customBulkSelectBanner] Replaces the built-in bulk select banner
 * @param {Array} [props.selectedRows] Ids (row[index] values) of the selected rows
 * @param {boolean} [props.reorderable] Drag-and-drop row reordering; disables sorting
 * @param {Function} [props.handleReorder] Called with (dragResult, activePage, rowsPerPage) after a drag ends
 * @param {node} [props.bulkSelectAction] Action button(s) rendered in the bulk select banner
 * @param {Function} [props.handleSelectAll] Called with the rows to select: the current page's rows from the header checkbox, or every filtered row from the bulk select banner link
 * @param {Function} [props.handleDeselectAll] Clears the selection
 * @param {boolean} [props.dataGrid] Full-height scrolling grid with cell borders and a sticky header; columns marked `sticky` pin to the left edge
 * @param {object[]} [props.filterBarSchema] Renders a FilterBar above the table with these filter definitions
 * @param {node} [props.filterBarActions] Trailing content in the FilterBar
 * @param {Function} [props.updateRowIdsRendered] Receives the ids of the currently rendered rows
 * @param {string} [props.itemName] Noun used in the pagination and bulk select labels
 * @param {boolean} [props.disabledSelection] Disables the bulk select checkboxes
 * @param {boolean} [props.expandable] Adds a chevron column that expands rows with expandedContent, expandedRows, or childTableData
 * @param {boolean} [props.showDescription] Info tooltip icon next to headers with a column description
 * @param {node} [props.bottomCaption] Caption rendered under the table
 * @param {object} [props.wrapCss] Inline style for the scroll wrapper (ignored with dataGrid)
 * @param {Function} [props.onSearch] Called with (accessor, value) when a column search changes
 * @param {Function} [props.onSearchOpen] Called when a column text search is opened
 * @param {string[]} [props.loadingSearchAccessors] Accessors whose column search shows a loading state
 */
export const AdvancedTable = ({
  testid = 'advanced-table',
  rows = [],
  columns = [],
  loading = false,
  loadingText,
  actions = [],
  defaultOrderBy = 'id',
  defaultOrder = 'asc',
  sortEnabled = true,
  index = 'id',
  displayHeader = true,
  pagination = false,
  rowsPerPage = 20,
  enableUpdateRowsPerPage = true,
  stickyAction = false,
  truncate = false,
  wrapColumnContent = true,
  hoverable = true,
  noDataText,
  noDataHeader,
  noDataAction,
  striped = false,
  borderlessRows = false,
  celled = false,
  compact = false,
  compactHeader = false,
  pinned,
  pinnedReverse = false,
  bulkSelectable,
  handleBulkSelectSingle,
  customBulkSelectBanner,
  selectedRows,
  reorderable = false,
  handleReorder,
  bulkSelectAction,
  handleSelectAll,
  handleDeselectAll,
  dataGrid = false,
  filterBarSchema = [],
  filterBarActions = null,
  updateRowIdsRendered,
  itemName,
  disabledSelection,
  expandable,
  showDescription = false,
  bottomCaption,
  wrapCss,
  onSearch,
  onSearchOpen,
  loadingSearchAccessors = [],
}) => {
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(rowsPerPage)
  const [activePage, setActivePage] = useState(1)
  const [order, setOrder] = useState(defaultOrder || 'asc')
  const [orderBy, setOrderBy] = useState(defaultOrderBy || 'id')
  const [searchFilters, setSearchFilters] = useState({})
  const [expandedRows, setExpandedRows] = useState([])

  const handleExpand = (rowId) => {
    if (expandedRows.includes(rowId)) {
      setExpandedRows(expandedRows.filter((id) => id !== rowId))
    } else {
      setExpandedRows([...expandedRows, rowId])
    }
  }

  const handleUpdateRowsPerPage = (value) => {
    setActivePage(1)
    setSelectedRowsPerPage(value)
  }

  const handleRequestSort = (event, accessor) => {
    const isAsc = orderBy === accessor && order === 'asc'

    // Reset active page on sort
    setActivePage(1)
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(accessor)
  }

  const updateSearch = (accessor, value) => {
    // Reset active page on search
    setActivePage(1)
    setSearchFilters((prevState) => ({
      ...prevState,
      [accessor]: value,
    }))
    if (onSearch) {
      onSearch(accessor, value)
    }
  }

  const isBulkSelectable =
    bulkSelectable && selectedRows && handleBulkSelectSingle && bulkSelectAction
  const someRowsSelected = !!(isBulkSelectable && !isEmpty(selectedRows))
  const allRowsSelected = someRowsSelected && rows.length === selectedRows.length
  const showBulkSelectBanner = !customBulkSelectBanner && someRowsSelected

  const tableOffset = calculateDataGridOffsets(dataGrid, someRowsSelected)

  // Filter all rows by search value
  const searchFilteredRows = filterRows(rows, searchFilters)

  // Find pagination values
  const count = searchFilteredRows.length
  const calculatedRowsPerPage = pagination ? selectedRowsPerPage : count
  const totalPages = pagination ? Math.ceil(count / calculatedRowsPerPage) : 1

  // Apply sort and pagination
  let filteredRows
  if (!reorderable && sortEnabled) {
    // reorderable is always sort disabled
    if (pinned) {
      // `pinned` is the accessor of the column that should always be sorted first
      // first do the regular sort on the rows, then partition to get pinned and unpinned
      const sortedRows = stableSort(searchFilteredRows, getComparator(order, orderBy))
      // checking if object because sometimes a user can pass in `{ as: <Component>, value: 'value' }`
      const [pinnedRows, unPinnedRows] = partition(sortedRows, (item) =>
        typeof item[pinned] === 'object' ? item[pinned].value : !!item[pinned]
      )

      let allRows
      if (pinnedReverse) {
        allRows = [...unPinnedRows, ...pinnedRows]
      } else {
        allRows = [...pinnedRows, ...unPinnedRows]
      }

      filteredRows = allRows.slice(
        (activePage - 1) * calculatedRowsPerPage,
        activePage * calculatedRowsPerPage
      )
    } else {
      filteredRows = stableSort(searchFilteredRows, getComparator(order, orderBy)).slice(
        (activePage - 1) * calculatedRowsPerPage,
        activePage * calculatedRowsPerPage
      )
    }
  } else {
    filteredRows = searchFilteredRows.slice(
      (activePage - 1) * calculatedRowsPerPage,
      activePage * calculatedRowsPerPage
    )
  }

  const handleSelectAllSearchFiltered = () => {
    handleSelectAll(searchFilteredRows)
  }

  const handleSelectPageSearchFiltered = () => {
    handleSelectAll(filteredRows)
  }

  const handleBulkDeselect = () => {
    handleDeselectAll()
  }

  useEffect(() => {
    // Reset table if there's a reload
    if (loading) {
      setSearchFilters({})
      setActivePage(1)
    }
  }, [loading])

  useEffect(() => {
    // A shrinking row set (filter change, deletion) can leave the held page
    // past the end, which slices to nothing and renders an empty table.
    if (pagination && activePage > totalPages) {
      setActivePage(Math.max(1, totalPages))
    }
  }, [pagination, activePage, totalPages])

  // Update the default order by if it has changed between renders
  useEffect(() => {
    setOrderBy(defaultOrderBy)
  }, [defaultOrderBy])

  useEffect(() => {
    setOrder(defaultOrder)
  }, [defaultOrder])

  useEffect(() => {
    if (updateRowIdsRendered) {
      updateRowIdsRendered(filteredRows.map((row) => row[index]))
    }
  }, [updateRowIdsRendered, filteredRows])

  if (loading) {
    return (
      <div className="empty-table">
        <Loader data-testid="loader" label={loadingText || ''} />
      </div>
    )
  }

  if (isEmpty(rows)) {
    return (
      <div className={classNames('empty-table', { wide: dataGrid })} data-testid="empty-table">
        <NoData text={noDataText} headerText={noDataHeader} />
        {noDataAction && <div data-testid="no-data-action">{noDataAction}</div>}
      </div>
    )
  }

  return (
    <>
      {!isEmpty(filterBarSchema) && (
        <FilterBar
          filterBarSchema={filterBarSchema}
          loading={loading}
          searchFilters={searchFilters}
          updateSearch={updateSearch}
          rows={rows}
        >
          {filterBarActions}
        </FilterBar>
      )}

      {customBulkSelectBanner}
      {showBulkSelectBanner && (
        <BulkSelectBanner
          dataGrid={dataGrid}
          handleBulkDeselect={handleBulkDeselect}
          handleSelectAll={handleSelectAllSearchFiltered}
          selectedRows={selectedRows}
          searchFilteredRows={searchFilteredRows}
          rows={rows}
          rowsPerPage={rowsPerPage}
          bulkSelectAction={bulkSelectAction}
          itemName={itemName}
          pagination={pagination}
          disabledSelection={disabledSelection}
        />
      )}

      <div
        className={classNames({
          'scrollable-table': !dataGrid,
          'data-grid': dataGrid,
          'has-bulk-select-banner': showBulkSelectBanner,
        })}
        style={dataGrid ? { height: `calc(100vh - ${tableOffset}px)` } : wrapCss}
      >
        <table
          className={classNames('advanced-table', {
            striped,
            hoverable,
            compact,
            celled,
          })}
          data-testid={testid}
        >
          {displayHeader && (
            <AdvancedTableHeader
              actions={actions}
              columns={columns}
              orderBy={orderBy}
              order={order}
              onRequestSort={handleRequestSort}
              displayHeader={displayHeader}
              borderlessRows={borderlessRows}
              truncate={truncate}
              stickyAction={stickyAction}
              updateSearch={updateSearch}
              wrapColumnContent={wrapColumnContent}
              searchFilters={searchFilters}
              compactHeader={compactHeader}
              isBulkSelectable={isBulkSelectable}
              expandable={expandable}
              allRowsSelected={allRowsSelected}
              selectedRows={selectedRows}
              reorderable={reorderable}
              rows={rows}
              filteredRows={filteredRows}
              loadingSearchAccessors={loadingSearchAccessors}
              onSearchOpen={onSearchOpen}
              bulkSelectAction={bulkSelectAction}
              handleDeselectAll={handleBulkDeselect}
              handleSelectAll={handleSelectPageSearchFiltered}
              dataGrid={dataGrid}
              disabledSelection={disabledSelection}
              showDescription={showDescription}
            />
          )}
          <DragDropContext
            onDragEnd={(result) => handleReorder(result, activePage, calculatedRowsPerPage)}
          >
            <Droppable droppableId="reorderable-table" isDropDisabled={!reorderable}>
              {(dropProvided) => (
                <AdvancedTableBody
                  filteredRows={filteredRows}
                  columns={columns}
                  actions={actions}
                  index={index}
                  borderlessRows={borderlessRows}
                  isBulkSelectable={isBulkSelectable}
                  selectedRows={selectedRows}
                  handleBulkSelectSingle={handleBulkSelectSingle}
                  reorderable={reorderable}
                  stickyAction={stickyAction}
                  wrapColumnContent={wrapColumnContent}
                  dropProvided={dropProvided}
                  searchFilters={searchFilters}
                  dataGrid={dataGrid}
                  disabledSelection={disabledSelection}
                  expandable={expandable}
                  handleExpand={handleExpand}
                  expandedRows={expandedRows}
                />
              )}
            </Droppable>
          </DragDropContext>
        </table>
        {bottomCaption && (
          <div className="bottom-caption-wrapper">
            <div className="bottom-caption">{bottomCaption}</div>
          </div>
        )}
      </div>
      {pagination && (
        <AdvancedTablePagination
          activePage={activePage}
          rowsPerPage={calculatedRowsPerPage}
          count={count}
          totalPages={totalPages}
          setActivePage={setActivePage}
          handleUpdateRowsPerPage={enableUpdateRowsPerPage && handleUpdateRowsPerPage}
          dataGrid={dataGrid}
          label={itemName}
        />
      )}
    </>
  )
}
